import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import {
  IJupyterRequeuePatientsResult,
  IJupyterSyncPatientResult,
  JupyterMessageChannel,
} from "../../../@types/admin/AdminJupyterRegistration";
import { jupyterService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/**
 * Requeue one JupyterHealth Exchange outbox message from the admin drill-down.
 * Invalidates the registrations query so the message shows as Pending again,
 * and resolves only once that refetch has landed — so the caller's busy state
 * covers it and the modal never shows the stale Failed row with Requeue enabled.
 */
export function useRequeueJupyterMessage() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      channel,
      messageId,
    }: {
      channel: JupyterMessageChannel;
      messageId: string;
    }) => ensureOk(await jupyterService.requeueMessage(channel, messageId)),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["admin-jupyter-registrations"],
      }),
  });

  return { requeueMessage: mutateAsync, isRequeueing: isPending };
}

/**
 * On-demand sync for one patient ("Sync now" on the Jupyter page). Refreshes
 * the registrations afterwards so the outbox statuses reflect the run.
 */
export function useSyncJupyterPatient() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, variables } = useMutation({
    mutationFn: async (patientId: number) =>
      ensureOk(await jupyterService.syncPatient(patientId)),
    onSettled: () => {
      // Refresh on failure too: a partial run may still have moved rows.
      queryClient.invalidateQueries({
        queryKey: ["admin-jupyter-registrations"],
      });
    },
  });

  return {
    syncPatient: mutateAsync,
    isSyncing: isPending,
    /** The patient a sync is currently running for, so only its row shows busy. */
    syncingPatientId: isPending ? variables : undefined,
  };
}

/** The API rejects a bulk requeue of more patients than this in one call. */
export const REQUEUE_PATIENTS_MAX_PER_CALL = 500;

/**
 * Requeues every failed message for one or more patients, splitting a large
 * selection into calls of at most {@link REQUEUE_PATIENTS_MAX_PER_CALL} and
 * summing the results — the selection spans pages and can exceed the API's cap.
 * Sequential, so a failure stops the rest and the error reaches the caller.
 */
async function requeueInChunks(
  patientIds: number[]
): Promise<IJupyterRequeuePatientsResult> {
  const total: IJupyterRequeuePatientsResult = {
    patients: [],
    observationsRequeued: 0,
    questionnairesRequeued: 0,
  };

  for (
    let start = 0;
    start < patientIds.length;
    start += REQUEUE_PATIENTS_MAX_PER_CALL
  ) {
    const chunk = patientIds.slice(
      start,
      start + REQUEUE_PATIENTS_MAX_PER_CALL
    );
    // eslint-disable-next-line no-await-in-loop
    const response = await jupyterService.requeuePatients(chunk);
    const result = ensureOk(response);
    total.patients.push(...result.patients);
    total.observationsRequeued += result.observationsRequeued;
    total.questionnairesRequeued += result.questionnairesRequeued;
  }

  return total;
}

/**
 * Requeue every failed message for one or more patients ("Requeue failed" on a
 * row, or on the page's selection). Refreshes the registrations afterwards so
 * the requeued messages show as Pending, and stays pending until that refetch
 * lands so the counts on screen are never the pre-requeue ones.
 */
export function useRequeueJupyterPatients() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, variables } = useMutation({
    mutationFn: requeueInChunks,
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: ["admin-jupyter-registrations"],
      }),
  });

  return {
    requeuePatients: mutateAsync,
    isRequeueing: isPending,
    /** The patients a requeue is running for, so only their rows show busy. */
    requeueingPatientIds: isPending ? variables : undefined,
  };
}

/** One patient's outcome in a bulk sync: the API's counters, or why the call failed. */
export type JupyterBulkSyncOutcome =
  | { patientId: number; result: IJupyterSyncPatientResult }
  | { patientId: number; error: unknown };

/** How many patients a bulk sync runs at once. Each sync makes live JHE calls. */
export const BULK_SYNC_CONCURRENCY = 3;

/**
 * Syncs several patients now by looping the per-patient Sync endpoint with a
 * small concurrency cap — each sync talks to JHE, so one server call for the
 * whole selection would hold a request open for minutes and report nothing
 * until the end. Tracks which patients are still in flight so their rows show
 * busy, and refreshes the registrations once, when every patient is done.
 */
export function useBulkSyncJupyterPatients() {
  const queryClient = useQueryClient();
  const [inFlight, setInFlight] = useState<ReadonlySet<number>>(new Set());
  const [isBulkSyncing, setIsBulkSyncing] = useState(false);

  const bulkSync = useCallback(
    async (patientIds: number[]): Promise<JupyterBulkSyncOutcome[]> => {
      const queue = [...patientIds];
      const outcomes: JupyterBulkSyncOutcome[] = [];
      setIsBulkSyncing(true);
      setInFlight(new Set(patientIds));

      const worker = async () => {
        for (
          let patientId = queue.shift();
          patientId !== undefined;
          patientId = queue.shift()
        ) {
          try {
            // Sequential within a worker on purpose: the cap is the point.
            // eslint-disable-next-line no-await-in-loop
            const response = await jupyterService.syncPatient(patientId);
            const result = ensureOk(response);
            outcomes.push({ patientId, result });
          } catch (error) {
            outcomes.push({ patientId, error });
          }
          const done = patientId;
          setInFlight((prev) => {
            const next = new Set(prev);
            next.delete(done);
            return next;
          });
        }
      };

      try {
        await Promise.all(
          Array.from(
            { length: Math.min(BULK_SYNC_CONCURRENCY, patientIds.length) },
            worker
          )
        );
      } finally {
        setIsBulkSyncing(false);
        setInFlight(new Set());
        queryClient.invalidateQueries({
          queryKey: ["admin-jupyter-registrations"],
        });
      }

      // Report in the order the patients were selected, not completion order.
      const order = new Map(patientIds.map((id, index) => [id, index]));
      return outcomes.sort(
        (a, b) => (order.get(a.patientId) ?? 0) - (order.get(b.patientId) ?? 0)
      );
    },
    [queryClient]
  );

  return { bulkSync, isBulkSyncing, bulkSyncingPatientIds: inFlight };
}
